import { useEffect, useState } from "react";
import { KeyRound, Copy, Loader2, Ban, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function ApiKeys() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/api-keys").then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/admin/api-keys", { name: name || "make-ingestion" });
      setNewKey(data.api_key);
      setName("");
      load();
      toast.success("Clave creada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta clave? Dejará de funcionar inmediatamente en Make.")) return;
    await api.delete(`/admin/api-keys/${id}`);
    toast.success("Clave revocada");
    load();
  };

  return (
    <div data-testid="apikeys-page" className="max-w-3xl">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">Claves API</h1>
      <p className="text-sm text-slate-500 mb-6">
        Para la ingesta automatizada desde Make: <span className="font-mono2 text-xs">POST /api/observatory/reports</span> con cabecera{" "}
        <span className="font-mono2 text-xs">X-API-Key</span>.
      </p>

      <form onSubmit={create} className="card-glow rounded-lg p-4 flex gap-3 mb-6" data-testid="apikey-create-form">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre de la clave (p. ej. make-diario)"
          data-testid="apikey-name-input"
          className="flex-1 bg-[#090a0f] border border-[#232838] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#8b5cf6] transition-colors"
        />
        <button type="submit" disabled={creating} data-testid="apikey-create-button"
          className="flex items-center gap-2 bg-[#8b5cf6] hover:bg-[#7c3aed] disabled:opacity-50 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Crear
        </button>
      </form>

      {newKey && (
        <div className="card-glow rounded-lg p-5 mb-6 border-[#8b5cf6] animate-fade-up" data-testid="apikey-new-banner">
          <p className="text-xs text-slate-400 mb-2">Copia esta clave ahora — no se volverá a mostrar:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono2 text-xs text-[#34d399] bg-[#090a0f] border border-[#232838] rounded-md px-3 py-2.5 break-all" data-testid="apikey-new-value">
              {newKey}
            </code>
            <button onClick={() => { navigator.clipboard.writeText(newKey); toast.success("Copiada"); }} data-testid="apikey-copy-button"
              className="p-2.5 rounded-md bg-[#1a1d29] border border-[#3b4259] hover:border-[#8b5cf6] transition-colors">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12" data-testid="apikeys-loading"><Loader2 className="w-6 h-6 animate-spin text-[#8b5cf6]" /></div>
      ) : items.length === 0 ? (
        <div className="card-glow rounded-lg p-10 text-center" data-testid="apikeys-empty">
          <KeyRound className="w-7 h-7 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Aún no hay claves. Crea una para conectar Make.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="apikeys-list">
          {items.map((k) => (
            <div key={k.id} className="card-glow rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid={`apikey-row-${k.name}`}>
              <div className="flex-1 min-w-40">
                <div className="text-sm font-medium">{k.name}</div>
                <div className="font-mono2 text-xs text-slate-500">{k.prefix}</div>
              </div>
              <div className="text-xs text-slate-500">
                <div>Creada: {k.created_at ? new Date(k.created_at).toLocaleDateString("es-ES") : "—"}</div>
                <div>Último uso: {k.last_used_at ? new Date(k.last_used_at).toLocaleString("es-ES") : "nunca"}</div>
              </div>
              {k.revoked ? (
                <span className="text-[10px] font-mono2 uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-950/50 border border-red-900 text-red-400" data-testid={`apikey-revoked-${k.name}`}>Revocada</span>
              ) : (
                <button onClick={() => revoke(k.id)} data-testid={`apikey-revoke-${k.name}`}
                  className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 border border-red-900/60 hover:border-red-700 rounded-md px-3 py-1.5 transition-colors">
                  <Ban className="w-3.5 h-3.5" /> Revocar
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
